import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink, Banknote, ArrowLeft } from "lucide-react";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import HostEarningsTable from "../components/HostEarningsTable";

/* HostPayoutsPage
 * ---------------
 * Single screen where hosts can:
 *   1. Create their Stripe Connect Express account (idempotent)
 *   2. Open Stripe's hosted onboarding (KYC + bank linking)
 *   3. See live status (charges_enabled / payouts_enabled / requirements due)
 *   4. Open their Express dashboard to view payouts
 *
 * All heavy lifting is on Stripe's hosted pages so this view stays tiny.
 */

export default function HostPayoutsPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [linkingOnboard, setLinkingOnboard] = useState(false);
  const [linkingDash, setLinkingDash] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos leer el estado de Stripe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/stripe/connect/status")
        .then(({ data }) => { if (!cancelled) setStatus(data); })
        .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "No pudimos leer el estado de Stripe"); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const handleCreateAccount = async () => {
    setCreating(true);
    try {
      await api.post("/stripe/connect/account");
      await refresh();
      // Immediately open onboarding — that's the next step anyway.
      await handleOpenOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos crear tu cuenta");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenOnboarding = async () => {
    setLinkingOnboard(true);
    try {
      const { data } = await api.post("/stripe/connect/onboarding-link");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos abrir el onboarding");
    } finally {
      setLinkingOnboard(false);
    }
  };

  const handleOpenDashboard = async () => {
    setLinkingDash(true);
    try {
      const { data } = await api.post("/stripe/connect/dashboard-link");
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos abrir el dashboard");
    } finally {
      setLinkingDash(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="text-neutral-600">Inicia sesión para administrar tus pagos.</p>
      </div>
    );
  }

  const ready = status?.charges_enabled && status?.payouts_enabled;
  const stripeReturn = new URLSearchParams(window.location.search).get("stripe");

  return (
    <div className="min-h-screen bg-sand-light pb-24" data-testid="host-payouts-page">
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <Link
          to="/host/dashboard"
          className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-agave mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al panel de anfitrión
        </Link>

        <div className="card-base">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
              <Banknote className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-agave">
                Pagos a anfitriones
              </h1>
              <p className="text-sm text-neutral-600 mt-1">
                Conecta tu cuenta bancaria con Stripe para recibir el 85% de cada
                reserva. Los depósitos se programan aproximadamente 7 días o según Stripe.
              </p>
            </div>
          </div>

          {stripeReturn === "return" && (
            <div className="mb-4 p-3 rounded-xl bg-sky-50 border border-sky-200 text-sky-900 text-sm">
              Acabamos de regresar de Stripe. Refrescando tu estado…
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : !status?.connected ? (
            <>
              <ul className="text-sm text-neutral-700 space-y-2 mb-6">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Tarda menos de 5 minutos</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> KYC validado por Stripe, no necesitamos guardar tu CLABE</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Pagos en pesos mexicanos (MXN), aproximadamente 7 días o según Stripe</li>
              </ul>
              <Button
                onClick={handleCreateAccount}
                disabled={creating || linkingOnboard}
                data-testid="connect-create-account-btn"
                className="w-full bg-terracotta hover:bg-terracotta/90 h-11"
              >
                {creating || linkingOnboard ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Conectar mi cuenta bancaria
              </Button>
            </>
          ) : ready ? (
            <>
              <div className="mb-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-start gap-2" data-testid="connect-ready-banner">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-emerald-900 text-sm">
                    Cuenta verificada y activa
                  </p>
                  <p className="text-xs text-emerald-800/80 mt-0.5">
                    Recibirás el pago de cada renta aproximadamente 7 días o según Stripe.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleOpenDashboard}
                disabled={linkingDash}
                data-testid="connect-dashboard-btn"
                variant="outline"
                className="w-full h-11"
              >
                {linkingDash ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Abrir mi dashboard de Stripe
              </Button>
            </>
          ) : (
            <>
              <div className="mb-5 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="connect-pending-banner">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-amber-900 text-sm">
                    Onboarding incompleto
                  </p>
                  <p className="text-xs text-amber-800/80 mt-0.5">
                    Stripe necesita más información para activar tus pagos.
                    {status?.currently_due?.length > 0 ? (
                      <> Faltan: {status.currently_due.slice(0, 4).join(", ")}.</>
                    ) : null}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleOpenOnboarding}
                disabled={linkingOnboard}
                data-testid="connect-resume-btn"
                className="w-full bg-terracotta hover:bg-terracotta/90 h-11"
              >
                {linkingOnboard ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Continuar onboarding
              </Button>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-neutral-200 flex items-center justify-between text-xs text-neutral-500">
            <span>Procesado de forma segura por Stripe</span>
            {status?.connected ? (
              <button
                type="button"
                onClick={refresh}
                className="hover:text-agave underline"
                data-testid="connect-refresh-btn"
              >
                Refrescar estado
              </button>
            ) : null}
          </div>
        </div>

        {/* Earnings table appears once the host is connected */}
        {status?.connected ? <HostEarningsTable /> : null}
      </div>
    </div>
  );
}
